import { Alert, LinearProgress } from "@mui/material";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useHealthRecord } from "../../../../store";
import { useAppContext } from "../../../hooks/useAppContext";
import { useConfig } from "../../../hooks/useConfig";
import { useFetchHealthRecord } from "../../../hooks/useFetchHealthRecord";
import { useI18n } from "../../../hooks/useI18n";
import { useApis } from "../../../providers/Dependencies";
import { encodeTravelDiagnosticData, TravelSnapshot } from "../travel/travelTypes";

export function IframeDiagnostic({ travel }: { travel?: TravelSnapshot }) {
  const { id } = useParams() as { id: string };
  const { organizationId, teamId } = useAppContext();
  const { locale, t } = useI18n();
  const { sadvIframeUrl } = useConfig();
  const apis = useApis();
  const healthRecord = useHealthRecord(id);
  const [evcToken, setEvcToken] = useState<string | undefined>(undefined);
  const [iframeHeight, setIframeHeight] = useState<number>(500);
  const [isEvcLoading, setIsEvcLoading] = useState<boolean>(true);
  const [completedIframeSrc, setCompletedIframeSrc] = useState<string>();
  const [evcError, setEvcError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useFetchHealthRecord(id);

  const mentorLocale = useMemo(() => {
    switch (locale) {
      case "fr":
        return "fr";
      case "en-gb":
        return "en";
      default:
        return "en";
    }
  }, [locale]);

  const iframeUrl = useMemo(() => {
    if (!sadvIframeUrl) return "";

    const url = new URL(sadvIframeUrl);
    url.pathname = url.hostname.includes("sadv-iframe") ? "/" : "/iframe";
    url.search = "";
    url.hash = "";
    return url.toString();
  }, [sadvIframeUrl]);

  const iframeSrc = useMemo(() => {
    if (!iframeUrl || !evcToken) return "";

    const url = new URL(iframeUrl);
    url.searchParams.set("frozen", "true");
    url.searchParams.set("lang", mentorLocale);
    url.searchParams.set("prevent-iframe-scroll", "true");
    url.searchParams.set("evc", evcToken);
    url.searchParams.set("pro", "true");
    if (travel) {
      url.searchParams.set("travel-data", encodeTravelDiagnosticData(travel));
    } else {
      url.searchParams.set("step", "recommandations");
    }
    return url.toString();
  }, [evcToken, iframeUrl, mentorLocale, travel]);

  useLayoutEffect(() => {
    if (!iframeUrl || !iframeSrc) return;

    const expectedOrigin = new URL(iframeUrl).origin;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (typeof event.data !== "object" || event.data === null) return;

      switch (event.data.type) {
        case "documentOpen":
          if (event.data?.data?.url) {
            window.open(event.data.data.url, "_blank", "noopener,noreferrer");
          }
          break;
        case "heightChange":
          if (event.data?.data?.height && typeof event.data.data.height === "number") {
            setIframeHeight(Math.max(event.data.data.height + 20, 500));
          }
          break;
        case "diagnosticLoadingCompleted":
          setCompletedIframeSrc(iframeSrc);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeSrc, iframeUrl]);

  useEffect(() => {
    let cancelled = false;
    const fetchEvcToken = async () => {
      try {
        setIsEvcLoading(true);
        setEvcError(false);

        const evcResponse =
          organizationId && teamId
            ? await apis.team.healthRecordApi.getEvcToken(organizationId, teamId, id)
            : await apis.pro.healthRecordApi.getEvcToken(id);

        if (!cancelled) setEvcToken(evcResponse.jwt);
      } catch (error) {
        console.error("Error fetching EVC token:", error);
        if (!cancelled) {
          setEvcToken(undefined);
          setEvcError(true);
        }
      } finally {
        if (!cancelled) setIsEvcLoading(false);
      }
    };

    fetchEvcToken();
    return () => {
      cancelled = true;
    };
  }, [apis, id, organizationId, teamId]);

  const isLoading = isEvcLoading || !iframeSrc || completedIframeSrc !== iframeSrc;

  if (evcError) {
    return <Alert severity="error">{t("common.errors.default")}</Alert>;
  }

  if (!healthRecord) {
    return <LinearProgress variant="query" />;
  }

  return (
    <div style={{ position: "relative", minHeight: 200 }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 10,
          }}
        >
          <LinearProgress variant="query" />
        </div>
      )}
      <iframe
        key={iframeSrc}
        ref={iframeRef}
        src={iframeSrc || undefined}
        width="100%"
        height={iframeHeight}
        title="SADV iframe"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        style={{ border: 0, opacity: isLoading ? 0 : 1, transition: "opacity 0.3s" }}
      />
    </div>
  );
}
